/* eslint-disable no-nested-ternary */
import './BootstrapTable.scss';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';

export interface PaginationConfig {
  type: 'pages' | 'scroll';
  totalPerPage?: number;
}

export interface BootstrapTableProps {
  data: Record<any, any>[];
  columns: {
    title: string;
    dataIndex?: string;
    titleClassName?: string;
    sorterFunction?: (a: RowType, b: RowType) => number;
    filterFunction?: (row: RowType) => boolean;
    columnClassName?: string;
    render?: (row: RowType) => JSX.Element | string;
  }[];
  striped?: boolean;
  bordered?: boolean;
  hover?: boolean;
  className?: string;
  pagination?: PaginationConfig;
  maxHeight?: string | number;
}

export type RowType = Record<string, unknown>;

export function BootstrapTable({
  data,
  columns,
  striped = true,
  bordered = true,
  hover = true,
  className = '',
  pagination,
  maxHeight = 400,
}: BootstrapTableProps) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [visibleCount, setVisibleCount] = useState<number>(pagination?.totalPerPage || 10);
  const [currentPage, setCurrentPage] = useState(1);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Aplica filtros e ordenação
  const processedData = useMemo(() => {
    let filteredData = [...data];

    // eslint-disable-next-line no-restricted-syntax
    for (const col of columns) {
      if (col.filterFunction) {
        filteredData = filteredData.filter((row) => col.filterFunction!(row));
      }
    }

    if (sortColumn) {
      const col = columns.find((c) => c.title === sortColumn);
      if (col && col.sorterFunction) {
        filteredData.sort((a, b) => {
          const result = col.sorterFunction!(a, b);
          return sortDirection === 'asc' ? result : -result;
        });
      }
    }

    return filteredData;
  }, [data, columns, sortColumn, sortDirection]);

  // Paginação
  const totalPerPage = pagination?.totalPerPage || 10;
  const totalPages =
    pagination?.type === 'pages' ? Math.ceil(processedData.length / totalPerPage) : 1;

  let paginatedData = processedData;

  if (pagination?.type === 'pages') {
    const start = (currentPage - 1) * totalPerPage;
    const end = start + totalPerPage;
    paginatedData = processedData.slice(start, end);
  }

  if (pagination?.type === 'scroll') {
    paginatedData = processedData.slice(0, visibleCount);
  }

  // Scroll infinito
  useEffect(() => {
    if (pagination?.type !== 'scroll') return;
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

      if (isBottom && visibleCount < processedData.length) {
        const newCount = Math.min(visibleCount + totalPerPage, processedData.length);
        setVisibleCount(newCount);
      }
    };

    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => container.removeEventListener('scroll', handleScroll);
  }, [pagination, processedData, visibleCount, totalPerPage]);

  // Ordenação
  const handleSort = (colTitle: string) => {
    if (sortColumn === colTitle) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colTitle);
      setSortDirection('asc');
    }
  };

  return (
    <div className={`bootstrap-table-wrapper ${className}`}>
      <div
        className={`table-container ${pagination?.type === 'scroll' ? 'scrollable' : ''}`}
        style={{ maxHeight }}
        ref={tableContainerRef}
      >
        <Table striped={striped} bordered={bordered} hover={hover} className="bootstrap-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = sortColumn === col.title;
                const indicator = isSorted ? (sortDirection === 'asc' ? '▲' : '▼') : '';
                return (
                  <th
                    key={col.title}
                    className={`table-header ${col.titleClassName || ''}`}
                    onClick={() => col.sorterFunction && handleSort(col.title)}
                  >
                    {col.title}
                    {col.sorterFunction && <span className="sort-indicator">{indicator}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index}>
                {columns.map((col) => {
                  if (col.render)
                    return (
                      <td className={col.columnClassName} key={col.title}>
                        {col.render(row)}
                      </td>
                    );
                  if (col.dataIndex)
                    return (
                      <td key={col.title} className={col.columnClassName}>
                        {String(row[col.dataIndex] ?? '')}
                      </td>
                    );
                  return <td key={col.title}></td>;
                })}
              </tr>
            ))}
          </tbody>
        </Table>

        {pagination?.type === 'scroll' && visibleCount < processedData.length && (
          <div className="loading-more">Carregando mais...</div>
        )}
      </div>

      {pagination?.type === 'pages' && totalPages > 1 && (
        <div className="pagination-controls">
          {Array.from({ length: totalPages }, (_, i) => {
            const page = i + 1;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                className={`page-btn ${isActive ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
